import { DialogActions, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import useProjects from '../project/context/useProjects';
import Button, { WhiteButton } from '../ui/Button';
import LoadingButton from '../ui/LoadingButton';
import useReport from './context/useReport';
import { Project } from '@gameonly/core';
import Loader from '../ui/Loader';

interface ProjectSelectionProps {
  onClose: () => void;
  onNext: () => void;
  setSelectedProject: (project: Project | null) => void;
}

const ProjectSelection = ({ onClose, onNext, setSelectedProject }: ProjectSelectionProps) => {
  const { t } = useTranslation(['cnc-reports', 'common', 'project']);
  const { projects, loading } = useProjects({
    projectType: 'site',
    validatedOrFinalized: true,
    isProratized: false,
  });
  const { reportId } = useParams();
  const { report } = useReport(reportId);

  const availableProjects = projects?.filter(
    (project) => !report?.locals.some((local) => local.id === project._id),
  );

  if (loading) {
    return <Loader fullPage />;
  }

  if (!availableProjects?.length) {
    return <p>{t('create-modal-internal.no-projects')}</p>;
  }

  const validationSchema = Yup.object({
    projectId: Yup.string().required(t('create-modal-internal.projectId-required')),
  });

  return (
    <Formik
      initialValues={{ projectId: '' }}
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form>
          <Typography variant="h6">{t('create-modal-internal.select-project')}</Typography>
          <Autocomplete
            options={availableProjects}
            getOptionLabel={(option) => option.name}
            onChange={(event, value) => {
              setFieldValue('projectId', value?._id || '');
              setSelectedProject(value || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('create-modal-internal.local')}
                margin="normal"
                fullWidth
                error={touched.projectId && !!errors.projectId}
                helperText={touched.projectId && errors.projectId}
              />
            )}
            value={availableProjects.find((p) => p._id.toString() === values.projectId) || null}
            isOptionEqualToValue={(option, value) => option._id === value._id}
          />
          <DialogActions>
            <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
            <LoadingButton type="submit" color="primary" disabled={!values.projectId}>
              {t('common:next')}
            </LoadingButton>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default ProjectSelection;
